import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Folder } from '@newgenus/common';

export type FolderAction = 'add' | 'rename' | 'delete';

export interface ManageFolderDialogData {
    action: FolderAction;
    /** The folder being renamed/deleted; undefined for 'add'. */
    folder?: Folder;
    /** Existing folder names (lower-cased compare) to prevent duplicates. */
    existingNames: string[];
}

export type ManageFolderDialogResult =
    | { action: 'add'; name: string }
    | { action: 'rename'; name: string }
    | { action: 'delete' }
    | undefined;

/**
 * Mat-dialog folder manager (replaces the removed NgBootstrap modal).
 * Add / rename / delete, with required-name + duplicate validation.
 */
@Component({
    selector: 'newgenus-manage-folder-dialog',
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
    ],
    template: `
        <h2 mat-dialog-title>{{ titleText }}</h2>

        <mat-dialog-content class="folder-form">
            <ng-container *ngIf="data.action !== 'delete'; else confirmDelete">
                <mat-form-field appearance="outline" class="w-100">
                    <mat-label>Folder name</mat-label>
                    <input
                        matInput
                        [(ngModel)]="name"
                        (ngModelChange)="error = ''"
                        maxlength="60"
                        data-cy="folder-name-input"
                        autocomplete="off"
                        cdkFocusInitial
                        (keyup.enter)="submit()"
                    />
                </mat-form-field>
                <div class="folder-error" *ngIf="error" data-cy="folder-error">{{ error }}</div>
            </ng-container>

            <ng-template #confirmDelete>
                <p class="delete-warning">
                    Delete the folder <strong>{{ data.folder?.name }}</strong
                    >? Emails filed here stay in their baskets — only the folder grouping is removed.
                </p>
            </ng-template>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close data-cy="folder-cancel">Cancel</button>
            <button
                *ngIf="data.action !== 'delete'"
                mat-raised-button
                color="primary"
                [disabled]="!name.trim()"
                (click)="submit()"
                data-cy="folder-confirm"
            >
                {{ data.action === 'add' ? 'Create' : 'Rename' }}
            </button>
            <button
                *ngIf="data.action === 'delete'"
                mat-raised-button
                color="warn"
                (click)="confirmDeletion()"
                data-cy="folder-delete-confirm"
            >
                Delete
            </button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .folder-form {
                min-width: 320px;
            }
            .w-100 {
                width: 100%;
            }
            .folder-error {
                color: #c62828;
                font-size: 0.82rem;
                margin-top: -0.5rem;
            }
            .delete-warning {
                margin: 0.25rem 0 0.5rem;
                line-height: 1.4;
            }
            @media (max-width: 767.98px) {
                .folder-form {
                    min-width: 0;
                }
            }
        `,
    ],
})
export class ManageFolderDialogComponent {
    public name = '';
    public error = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ManageFolderDialogData,
        private dialogRef: MatDialogRef<ManageFolderDialogComponent, ManageFolderDialogResult>
    ) {
        if (data.action === 'rename') this.name = data.folder?.name || '';
    }

    public get titleText(): string {
        switch (this.data.action) {
            case 'add':
                return 'New folder';
            case 'rename':
                return 'Rename folder';
            case 'delete':
                return 'Delete folder';
        }
    }

    public submit(): void {
        const name = this.name.trim();
        if (!name) {
            this.error = 'Please enter a folder name.';
            return;
        }
        // Renaming to the same name is a no-op, but allowed.
        const isSameAsCurrent =
            this.data.action === 'rename' && name.toLowerCase() === (this.data.folder?.name || '').toLowerCase();
        if (!isSameAsCurrent && this.data.existingNames.some((n) => n.toLowerCase() === name.toLowerCase())) {
            this.error = 'A folder with that name already exists.';
            return;
        }
        this.dialogRef.close({ action: this.data.action as 'add' | 'rename', name });
    }

    public confirmDeletion(): void {
        this.dialogRef.close({ action: 'delete' });
    }
}
